/**
 * DELDEMO — Persistent Demo Voucher page
 *
 * Route: /demo/:cardId  (reached via LandingPage scan-route when the card
 * is flagged `is_demo_card: true`)
 *
 * Behavior:
 *   - Stable: never redirects, never expires, no VIP timer.
 *   - PRESS VIP: reveals the voucher inline with celebration animation.
 *   - Download Voucher: client-side PNG render via <canvas>.
 *   - Reset Demo: gear icon → password dialog → POST /api/demo/{id}/reset.
 *
 * All state is local (useState). Voucher content fetched once from
 * GET /api/demo/{cardId}. Everything else is deterministic.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGuestTitle } from '../lib/guestRoute';
import { toast } from 'sonner';
import { Sparkles, Gift, Download, Settings, Loader2, X, RotateCcw, RefreshCw, Check, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { stringifyDetail } from '../lib/utils';
import { cardSurfaceClass } from '../components/shared/cardSurface';
import { LeadGenOfferScreen } from '../components/gift/LeadGenOfferScreen';
import { GiftEmailDelivery } from '../components/gift/GiftEmailDelivery';

const API = window.location.origin;

function ScratchReveal({ onComplete, label, logoUrl, logoOpacity, logoSize }) {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const started = useRef(false);
    const done = useRef(false);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width; canvas.height = rect.height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        // The metallic gradient, the brushed streaks and the SCRATCH HERE
        // label ARE the affordance — they are what tells the guest this is
        // foil to be scratched, not a picture. An optional brand mark is a
        // WATERMARK laid over them: capped at ~38% of the shorter side, drawn
        // at the merchant-set opacity, streaks re-laid on top, and kept out of
        // the label band so SCRATCH HERE is never obscured.
        const LABEL_BAND = 34;
        const streaks = (alpha) => {
            ctx.strokeStyle = `rgba(255,255,255,${alpha})`; ctx.lineWidth = 1;
            for (let i = -canvas.height; i < canvas.width; i += 7) {
                ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i + canvas.height, canvas.height); ctx.stroke();
            }
        };
        const drawLabel = (centred) => {
            ctx.fillStyle = 'rgba(30,27,50,0.55)';
            ctx.font = 'bold 16px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText((label || 'Scratch here').toUpperCase(),
                         canvas.width / 2,
                         centred ? canvas.height / 2 : canvas.height - 14);
        };
        const paintSurface = (img) => {
            ctx.globalCompositeOperation = 'source-over';
            ctx.globalAlpha = 1;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
            grad.addColorStop(0, '#eef0f5'); grad.addColorStop(0.25, '#c9cdd7');
            grad.addColorStop(0.5, '#9aa0ac'); grad.addColorStop(0.75, '#c9cdd7');
            grad.addColorStop(1, '#8b909c');
            ctx.fillStyle = grad; ctx.fillRect(0, 0, canvas.width, canvas.height);
            streaks(0.22);
            if (img) {
                // Merchant-set size, as a % of the shorter side (default 38).
                // The width cap scales WITH it — at 38% it is the historic
                // 0.494×width so every saved value renders exactly as before,
                // and from ~77% up a wide wordmark may span the full panel.
                // Height is still hard capped above the label band so SCRATCH
                // HERE stays clear at any size.
                const pct = Math.max(25, Math.min(100, Number(logoSize) || 38));
                const maxH = Math.min(Math.min(canvas.width, canvas.height) * pct / 100,
                                      canvas.height - LABEL_BAND - 6);
                const maxW = canvas.width * Math.min(1, (pct / 100) * 1.3);
                const scale = Math.min(maxW / img.width, maxH / img.height, 1);
                const w = img.width * scale;
                const h = img.height * scale;
                ctx.globalAlpha = Math.max(15, Math.min(60, Number(logoOpacity) || 30)) / 100;
                ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - LABEL_BAND - h) / 2, w, h);
                ctx.globalAlpha = 1;
                streaks(0.14);  // foil still reads as foil THROUGH the mark
            }
            drawLabel(!img);
            ctx.globalCompositeOperation = 'destination-out';
            // Under destination-out only the SOURCE ALPHA matters; use fully
            // opaque paint so every stroke actually clears the mask.
            ctx.fillStyle = 'rgba(0,0,0,1)';
            ctx.strokeStyle = 'rgba(0,0,0,1)';
            ctx.lineWidth = 46; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
        };
        // Paint the plain foil synchronously — the panel is never transparent.
        paintSurface(null);
        // The mark can only be composited ON TOP of the texture while the mask
        // is still off, so repaint once it decodes — and only while the guest
        // has not started scratching, so no progress is ever undone.
        if (logoUrl) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => { if (!started.current && !done.current) paintSurface(img); };
            img.src = logoUrl;
        }
        const pos = (e) => {
            const r = canvas.getBoundingClientRect();
            const t = e.touches && e.touches.length ? e.touches[0] : (e.changedTouches && e.changedTouches.length ? e.changedTouches[0] : e);
            return { x: t.clientX - r.left, y: t.clientY - r.top };
        };
        // Percentage of the scratch surface actually cleared (alpha === 0),
        // sampled across the whole canvas. Reveals at ~75% cleared.
        const check = () => {
            if (done.current) return;
            const img = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
            let clear = 0, total = 0;
            for (let i = 3; i < img.length; i += 40) { total++; if (img[i] === 0) clear++; }
            if (total && clear / total >= 0.75) { done.current = true; onComplete(); }
        };
        let last = null;
        // Draw a CONNECTED stroke (line + end cap) so fast drags leave no
        // uncleared gaps between points — the measured area then matches what
        // the guest actually sees.
        const paint = (p) => {
            if (last) { ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(p.x, p.y); ctx.stroke(); }
            ctx.beginPath(); ctx.arc(p.x, p.y, 23, 0, Math.PI * 2); ctx.fill();
            last = p;
        };
        let sinceCheck = 0;
        const scratch = (e) => {
            if (!drawing.current || done.current) return;
            paint(pos(e));
            e.preventDefault();
            if (++sinceCheck >= 3) { sinceCheck = 0; check(); }  // live reveal, no lift needed
        };
        const start = (e) => { drawing.current = true; started.current = true; last = pos(e); paint(pos(e)); };
        const end = () => { drawing.current = false; last = null; check(); };
        canvas.addEventListener('mousedown', start); canvas.addEventListener('mousemove', scratch);
        window.addEventListener('mouseup', end);
        canvas.addEventListener('touchstart', start, { passive: false });
        canvas.addEventListener('touchmove', scratch, { passive: false });
        canvas.addEventListener('touchend', end);
        return () => {
            canvas.removeEventListener('mousedown', start); canvas.removeEventListener('mousemove', scratch);
            window.removeEventListener('mouseup', end);
            canvas.removeEventListener('touchstart', start); canvas.removeEventListener('touchmove', scratch);
            canvas.removeEventListener('touchend', end);
        };
    }, [onComplete, label, logoUrl, logoOpacity, logoSize]);
    return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full cursor-pointer touch-none z-20" data-testid="gg-scratch-canvas" />;
}

export default function DemoVoucher() {
    const { cardId } = useParams();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const [voucher, setVoucher] = useState(null);
    const [loading, setLoading] = useState(true);
    const [revealed, setRevealed] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const [resetPassword, setResetPassword] = useState('');
    const [resetTitle, setResetTitle] = useState('');
    const [resetSubtitle, setResetSubtitle] = useState('');
    const [resetting, setResetting] = useState(false);
    const voucherRef = useRef(null);

    // ── Gift Generator (extends the existing voucher/VIP engine) ──
    // When the card's org has the Gift Generator enabled + in stock, this
    // guest surface becomes "Generate a VIP gift from us" instead of the
    // static PRESS VIP demo reveal.
    const [ggConfig, setGgConfig] = useState(null);   // {enabled, exchange_enabled, exchange_limit}
    const [ggPhase, setGgPhase] = useState('idle');   // idle | generating | revealed | accepting | delivering
    // Which delivery path the guest took: 'no_app' (voucher by email, Auto Flow
    // style) or 'app' (sponsor app code by email).
    const [deliveryPath, setDeliveryPath] = useState('no_app');
    const [ggSession, setGgSession] = useState(null); // {session_id, gift, can_exchange, exchanges_used, exchange_limit}
    const [ggScratched, setGgScratched] = useState(false);
    // Lead Generation — brand-neutral 2x artwork, shared by every campaign.
    // Deliberately generic: McDonald's is the first campaign using this, not
    // the feature's identity.
    const LEAD_GEN_2X_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/54c73f9e-5b01-48ed-8a36-6db023068760/images/522b7adee91d957669a576be4ef69b7bfb655cad11e8920ca4a5685ae99cccdd.jpeg';

    const [warmGiftUrl, setWarmGiftUrl] = useState(null);
    // Review Mode — launched from the Review Shield journey via query params.
    // In this flow the scratch starts immediately (no 5s "generating" screen)
    // and, after Accept + Save, the guest continues to the public review.
    const reviewParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const reviewFlow = reviewParams.get('flow') === 'review';
    const reviewFb = reviewParams.get('fb') || '';
    const reviewRedirect = reviewParams.get('rc') || '';
    // Preview Mode — merchant "Preview Gift Experience" from settings. Config
    // is passed via localStorage (shared across tabs); nothing is fetched and
    // Accept mints NO real voucher.
    const previewMode = reviewParams.get('preview') === '1';
    const [previewCfg] = useState(() => {
        if (typeof window === 'undefined') return null;
        try { return JSON.parse(localStorage.getItem('ggPreviewConfig') || 'null'); } catch { return null; }
    });
    const [previewExch, setPreviewExch] = useState(0);
    // Which branch the admin took in the preview, so the confirmation can show
    // the doubled prize they would actually have handed out.
    const [previewDoubled, setPreviewDoubled] = useState(false);

    const loadVoucher = useCallback(async () => {
        try {
            const res = await fetch(`${API}/api/demo/${encodeURIComponent(cardId)}`);
            if (!res.ok) throw new Error('load_failed');
            const data = await res.json();
            setVoucher(data);
        } catch {
            // Backend failure must not crash the demo. Use a safe fallback so the
            // page still loads — the page itself is the point of this endpoint.
            setVoucher({
                card_id: cardId,
                title: 'Complimentary Drink',
                subtitle: 'Courtesy of the house',
                footer: 'Valid for demonstration purposes',
                disclaimer: 'No cash value',
                is_demo: true,
            });
        } finally {
            setLoading(false);
        }
    }, [cardId]);

    useEffect(() => { if (!previewMode) loadVoucher(); else setLoading(false); }, [loadVoucher, previewMode]);

    // Probe Gift Generator availability for this card's org.
    useEffect(() => {
        if (previewMode) { setGgConfig(previewCfg); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API}/api/vouchers/gift/config/${encodeURIComponent(cardId)}`);
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled) setGgConfig(data);
            } catch { /* silent — falls back to static demo voucher */ }
        })();
        return () => { cancelled = true; };
    }, [cardId, previewMode, previewCfg]);

    const ggEnabled = !!ggConfig?.enabled;
    // Rewrite our own image URLs to a PRE-BAKED, CDN-cacheable variant:
    //   /api/voucher-settings/X-serve/{ids}?v=1  ->  .../{ids}/w440.avif?v=1
    // The extension must be in the PATH (not the query) and the response must
    // carry no `Vary` for Cloudflare to cache it at the edge — that is why the
    // format is chosen by URL rather than by the Accept header.
    // External/static URLs are left untouched.
    const sized = useCallback((url, w) => {
        if (!url || typeof url !== 'string') return url;
        if (!url.includes('/api/voucher-settings/')) return url;
        const [base, qs] = url.split('?');
        return `${base.replace(/\/$/, '')}/w${w}.avif${qs ? `?${qs}` : ''}`;
    }, []);
    // Rare old browser with no AVIF decode support → fall back to the WebP
    // variant of the same asset (same URL, different extension).
    const onImgFallback = useCallback((e) => {
        const el = e.currentTarget;
        if (el.dataset.fb) return;
        el.dataset.fb = '1';
        el.src = el.src.replace(/\.avif(\?|$)/, '.webp$1');
    }, []);
    // Guest wallpaper (merchant upload → premium default) + merchant logo.
    const ggWallpaperRaw = ggConfig?.wallpaper_url
        ? (ggConfig.wallpaper_url.startsWith('http') ? ggConfig.wallpaper_url : `${API}${ggConfig.wallpaper_url}`)
        : '/branding/gg-default-wallpaper.jpg';
    const ggWallpaper = sized(ggWallpaperRaw, 1200);
    // Optional scratch-surface logo (org/campaign upload). Null → default texture.
    const scratchLogoRaw = ggConfig?.scratch_surface_logo_url || null;
    const scratchLogo = scratchLogoRaw
        ? sized(scratchLogoRaw.startsWith('http') ? scratchLogoRaw : `${API}${scratchLogoRaw}`, 560)
        : null;
    // Merchant-tunable watermark strength (15–60%), defaulting to a subtle 30%.
    const scratchLogoOpacity = Math.max(15, Math.min(60, Number(ggConfig?.scratch_surface_logo_opacity) || 30));
    // Merchant-tunable watermark size, % of the panel's shorter side.
    const scratchLogoSize = Math.max(25, Math.min(100, Number(ggConfig?.scratch_surface_logo_size) || 38));
    const merchantLogoRaw = ggConfig?.merchant_logo_url
        ? (ggConfig.merchant_logo_url.startsWith('http') ? ggConfig.merchant_logo_url : `${API}${ggConfig.merchant_logo_url}`)
        : null;
    const merchantLogo = sized(merchantLogoRaw, 560);
    const merchantName = ggConfig?.organization_name || '';
    // Guest tab title = the merchant's name, never the portal's.
    useGuestTitle(merchantName);
    // Merchant-editable copy with i18n fallback + configurable logo size.
    const GG_COPY_DEFAULTS = {
        headlineLine1: 'heroUnlockedPre', headlineHighlight: 'heroUnlockedHighlight',
        tapForGift: 'tapForGift', generateButton: 'cta', generatingTitle: 'generating',
        generatingSub: 'generatingSub', revealTitle: 'revealTitle',
        scratchInstruction: 'scratchInstruction', acceptButton: 'accept',
        exchangeButton: 'exchange', keepPrompt: 'keepPrompt',
    };
    const _ggCopy = ggConfig?.copy || {};
    const _lang = (i18n.language || 'en').split('-')[0];
    const gx = (key) => (_ggCopy?.[_lang]?.[key] || _ggCopy?.en?.[key] || t(`giftGenerator.${GG_COPY_DEFAULTS[key]}`));
    const GG_LOGO_SIZE = { sm: 'h-6 sm:h-7', md: 'h-9 sm:h-10', lg: 'h-12 sm:h-14', xl: 'h-16 sm:h-20' };
    const ggLogoClass = GG_LOGO_SIZE[ggConfig?.logo_size] || GG_LOGO_SIZE.md;
    // Merchant-controlled logo size (px) — drives the customer-facing badge.
    const ggLogoPx = Math.max(24, Math.min(240, Number(ggConfig?.logo_size_px) || 48));
    // Existing org/card Card Style (Frosted/Clear/Solid) — reuse the SAME
    // shared surface system as Review Shield. No Gift-Generator-specific style.
    const ggCardSurface = cardSurfaceClass(ggConfig?.card_style, 'dark');

    // Preload the two above-the-fold critical assets (wallpaper + logo) as
    // early as config is known, so the browser fetches them in parallel
    // before React paints the <img> tags.
    useEffect(() => {
        if (!ggEnabled) return;
        const urls = [ggWallpaper, merchantLogo].filter(Boolean);
        const links = urls.map((href) => {
            const l = document.createElement('link');
            l.rel = 'preload'; l.as = 'image'; l.href = href;
            l.setAttribute('fetchpriority', 'high');
            document.head.appendChild(l);
            return l;
        });
        return () => links.forEach((l) => l.remove());
    }, [ggEnabled, ggWallpaper, merchantLogo]);

    // Warm the CANDIDATE reward images as soon as config resolves — the winning
    // gift is only picked at /generate, so preloading the (capped) in-stock pool
    // is what actually gets the artwork into cache before the reveal paints.
    // The old per-gift preload fired in the SAME React commit as the reveal
    // <img>, so it never won the race (measured: initiatorType "img", not "link").
    useEffect(() => {
        if (!ggEnabled) return;
        const urls = (ggConfig?.gift_image_urls || [])
            .map((u) => sized(u.startsWith('http') ? u : `${API}${u}`, 440))
            .filter(Boolean);
        const links = urls.map((href) => {
            const l = document.createElement('link');
            l.rel = 'preload'; l.as = 'image'; l.href = href;
            l.setAttribute('fetchpriority', 'low');
            document.head.appendChild(l);
            return l;
        });
        return () => links.forEach((l) => l.remove());
    }, [ggEnabled, ggConfig, sized]);

    // Prefetch the reserved gift image (set at generate time) with a preload
    // link so the reveal <img> reuses it — single download, instant reveal.
    useEffect(() => {
        if (!warmGiftUrl) return;
        const l = document.createElement('link');
        l.rel = 'preload'; l.as = 'image'; l.href = warmGiftUrl;
        document.head.appendChild(l);
        return () => l.remove();
    }, [warmGiftUrl]);

    // Generate → atomically RESERVE a gift server-side, hold the polished
    // ~5s "finding your gift" animation, then reveal.
    const handleGenerate = useCallback(async () => {
        if (previewMode) {
            // Local, server-free preview using the current (unsaved) config.
            const pool = (previewCfg?.gifts || []).filter(g => (g.remaining == null || Number(g.remaining) > 0));
            const list = pool.length ? pool : (previewCfg?.gifts || []);
            const pick = list.length ? list[Math.floor(Math.random() * list.length)] : { title: 'Sample Gift', description: '', value: 0 };
            setGgPhase('generating');
            const skip = previewCfg?.reveal_method === 'scratch';
            setTimeout(() => {
                setGgScratched(false);
                setGgSession({
                    session_id: 'preview',
                    gift: pick,
                    can_exchange: !!previewCfg?.exchange_enabled && (previewCfg?.gifts || []).length > 1,
                    exchange_limit: Number(previewCfg?.exchange_limit) || 0,
                    exchanges_used: previewExch,
                });
                setGgPhase('revealed');
            }, skip ? 0 : 1500);
            return;
        }
        setGgPhase('generating');
        const started = Date.now();
        try {
            // P_SURVEY_SCRATCH_WIN_1.0 — a scratch earned from a survey carries
            // a single-use token in the URL.
            const rewardToken = new URLSearchParams(window.location.search).get('reward_token');
            const genUrl = `${API}/api/vouchers/gift/generate/${encodeURIComponent(cardId)}`
                + (rewardToken ? `?reward_token=${encodeURIComponent(rewardToken)}` : '');
            const res = await fetch(genUrl, { method: 'POST' });
            const data = await res.json().catch(() => ({}));
            if (res.status === 403 && stringifyDetail(data.detail) === 'reward_token_required') {
                setGgPhase('reward_required');
                return;
            }
            if (!res.ok) throw new Error(stringifyDetail(data.detail) || 'generate_failed');
            // Warm the reward image during the "finding your gift" animation via
            // a preload link (browser reconciles it with the reveal <img> → the
            // reveal is instant AND the asset is downloaded only once).
            const gimg = data?.gift?.image_url;
            if (gimg) setWarmGiftUrl(sized(gimg.startsWith('http') ? gimg : `${API}${gimg}`, 440));
            // Skip the 5s "finding your gift" treatment for Scratch Reveal and
            // for Review Mode — the scratch ticket should be usable immediately.
            const skipDelay = reviewFlow || ggConfig?.reveal_method === 'scratch';
            const wait = skipDelay ? 0 : Math.max(0, 5000 - (Date.now() - started));
            setTimeout(() => { setGgScratched(false); setGgSession(data); setGgPhase('revealed'); }, wait);
        } catch {
            toast.error(t('giftGenerator.generateFailed'));
            setGgPhase('idle');
        }
    }, [cardId, t, reviewFlow, ggConfig, previewMode, previewCfg, previewExch]);

    // Exchange → release current reservation, reserve another. Server enforces
    // the exchange limit; we honor `can_exchange` from the response.
    const handleExchange = useCallback(async () => {
        if (!ggSession) return;
        if (previewMode) {
            const next = previewExch + 1;
            const pool = (previewCfg?.gifts || []);
            const pick = pool.length ? pool[next % pool.length] : ggSession.gift;
            setGgPhase('generating');
            setPreviewExch(next);
            setTimeout(() => {
                setGgScratched(false);
                setGgSession(s => ({ ...s, gift: pick, exchanges_used: next, can_exchange: !!previewCfg?.exchange_enabled && next < (Number(previewCfg?.exchange_limit) || 0) }));
                setGgPhase('revealed');
            }, previewCfg?.reveal_method === 'scratch' ? 0 : 900);
            return;
        }
        setGgPhase('generating');
        const started = Date.now();
        try {
            const res = await fetch(`${API}/api/vouchers/gift/exchange/${ggSession.session_id}`, { method: 'POST' });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                if (res.status === 403) toast.error(t('giftGenerator.exchangeLimit'));
                else throw new Error(stringifyDetail(data.detail) || 'exchange_failed');
                setGgPhase('revealed');
                return;
            }
            const wait = Math.max(0, 1800 - (Date.now() - started));
            const gimg = data?.gift?.image_url;
            if (gimg) setWarmGiftUrl(sized(gimg.startsWith('http') ? gimg : `${API}${gimg}`, 440));
            setTimeout(() => { setGgScratched(false); setGgSession(data); setGgPhase('revealed'); }, wait);
        } catch {
            toast.error(t('giftGenerator.exchangeFailed'));
            setGgPhase('revealed');
        }
    }, [ggSession, t, previewMode, previewExch, previewCfg]);

    // Accept → CONSUME inventory, mint the existing voucher object, then hand
    // off to the existing VoucherDisplay (code / QR / redemption / download).
    const handleAccept = useCallback(async (leadGeneration = false) => {
        if (!ggSession) return;
        if (previewMode) { setPreviewDoubled(!!leadGeneration); setGgPhase('previewAccepted'); return; }
        setGgPhase('accepting');
        try {
            const res = await fetch(`${API}/api/vouchers/gift/accept/${ggSession.session_id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // The server re-checks the org toggle; this only states which
                // path the buyer chose.
                body: JSON.stringify({ lead_generation: !!leadGeneration,
                                       // Non-review taps are delivered by email,
                                       // so the code must not come back here.
                                       email_delivery: !reviewFlow }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !(data?.voucher?.code || data?.delivery_required)) throw new Error(stringifyDetail(data.detail) || 'accept_failed');
            if (reviewFlow) {
                // Review Mode → carry the public-review redirect + feedback id
                // to the voucher page, which shows Save + "Continue to review".
                const q = new URLSearchParams();
                if (reviewRedirect) q.set('rc', reviewRedirect);
                if (reviewFb) q.set('fb', reviewFb);
                navigate(`/voucher/${data.voucher.code}?${q.toString()}`);
            } else {
                // TWO DELIVERY PATHS — the gift is emailed, never shown. The
                // guest captures their email once, here, after the choice.
                setDeliveryPath(leadGeneration ? 'app' : 'no_app');
                setGgPhase('delivering');
            }
        } catch {
            toast.error(t('giftGenerator.generateFailed'));
            setGgPhase('revealed');
        }
    }, [ggSession, navigate, t, reviewFlow, reviewRedirect, reviewFb, previewMode]);

    // ── Lead Generation (optional, per setup). The interstitial sits between
    // the reveal and the voucher, so nothing is minted until the buyer picks.
    // It runs in the ADMIN PREVIEW too — an admin turning Lead Generation on
    // needs to SEE the 2x step, and the preview's Accept mints nothing anyway.
    const leadGenOn = !!ggConfig?.lead_generation_enabled;

    // Guest-side funnel events. Fire-and-forget; a blocked beacon must never
    // interrupt the guest. Skipped in the admin preview (no real session).
    const ggTrack = useCallback((event, meta) => {
        const sid = ggSession?.session_id;
        if (previewMode || !sid) return;
        try {
            fetch(`${API}/api/vouchers/gift/event/${sid}`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ event, meta: meta || {} }), keepalive: true,
            }).catch(() => {});
        } catch { /* never block the guest */ }
    }, [ggSession, previewMode]);

    // The 2x offer impression — only ever in Path B.
    useEffect(() => {
        if (ggPhase === 'leadGen') ggTrack('leadgen_offer_shown');
    }, [ggPhase, ggTrack]);
    const giftIsMonetary = !!ggSession?.gift?.is_monetary;
    const doubledPrizeLabel = giftIsMonetary
        ? `$${(Number(ggSession?.gift?.value) || 0) * 2}`
        : `2× ${ggSession?.gift?.title || ''}`.trim();
    // Per-gift DOUBLED-prize artwork, uploaded by the merchant next to the
    // single-prize image. Falls back to the generic 2x graphic so a campaign
    // that hasn't uploaded one still works.
    const doubleImageRaw = ggSession?.gift?.double_image_url || null;
    // 880px = retina for a ~440px-wide card, so the doubled artwork is sharp on
    // an iPad/iPhone 3x screen instead of being upscaled.
    const leadGenImage = doubleImageRaw
        ? sized(doubleImageRaw.startsWith('http') ? doubleImageRaw : `${API}${doubleImageRaw}`, 880)
        : LEAD_GEN_2X_IMAGE;
    // .avif → .webp → generic graphic, so a missing/undecodable variant can
    // never leave the interstitial with an empty frame.
    const onLeadGenImgFallback = useCallback((e) => {
        const el = e.currentTarget;
        if (!el.dataset.fb && /\.avif(\?|$)/.test(el.src)) {
            el.dataset.fb = '1';
            el.src = el.src.replace(/\.avif(\?|$)/, '.webp$1');
            return;
        }
        if (el.src !== LEAD_GEN_2X_IMAGE) el.src = LEAD_GEN_2X_IMAGE;
    }, [LEAD_GEN_2X_IMAGE]);

    // Warm the doubled-prize artwork while the guest is still looking at the
    // reveal, so the interstitial paints instantly after Accept.
    useEffect(() => {
        if (!leadGenOn || ggPhase !== 'revealed' || !doubleImageRaw) return;
        const l = document.createElement('link');
        l.rel = 'preload'; l.as = 'image'; l.href = leadGenImage;
        document.head.appendChild(l);
        return () => l.remove();
    }, [leadGenOn, ggPhase, doubleImageRaw, leadGenImage]);

    // "Continue to get 2x" — mint the DOUBLED voucher first so the prize is    // already real, then hand them off to the campaign's link. The voucher
    // page is reached the same way it always is, from the voucher code.
    const handleLeadGenAccept = useCallback(async () => {
        const url = ggConfig?.lead_generation_url;
        ggTrack('leadgen_cta_clicked');
        // In the admin preview this is how they confirm the sponsor link works.
        if (url) { ggTrack('app_download_initiated', { url }); window.open(url, '_blank', 'noopener'); }
        await handleAccept(true);
    }, [ggConfig, handleAccept, ggTrack]);

    // Review Mode auto-starts the gift generation immediately (no idle screen).
    useEffect(() => {
        if (reviewFlow && ggEnabled && ggPhase === 'idle') handleGenerate();
    }, [reviewFlow, ggEnabled, ggPhase, handleGenerate]);

    /** Build a branded PNG of the voucher using a hidden canvas. */
    const downloadPng = async () => {
        if (!voucher) return;
        setDownloading(true);
        try {
            // 1080×1350 = vertical social format — prints & shares cleanly
            const W = 1080, H = 1350;
            const canvas = document.createElement('canvas');
            canvas.width = W; canvas.height = H;
            const ctx = canvas.getContext('2d');

            // Gradient background (fuchsia → purple)
            const bg = ctx.createLinearGradient(0, 0, W, H);
            bg.addColorStop(0, '#a21caf');
            bg.addColorStop(1, '#4c1d95');
            ctx.fillStyle = bg;
            ctx.fillRect(0, 0, W, H);

            // Inner rounded white card
            const pad = 80;
            const cardW = W - pad * 2;
            const cardH = H - pad * 2;
            const radius = 56;
            ctx.fillStyle = '#ffffff';
            roundRect(ctx, pad, pad, cardW, cardH, radius);
            ctx.fill();

            // Thin fuchsia border
            ctx.lineWidth = 6;
            ctx.strokeStyle = '#d946ef';
            roundRect(ctx, pad + 3, pad + 3, cardW - 6, cardH - 6, radius - 3);
            ctx.stroke();

            // "VIP REWARD" badge
            ctx.fillStyle = '#a21caf';
            ctx.font = 'bold 36px system-ui, -apple-system, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('✦ VIP REWARD ✦', W / 2, pad + 140);

            // Title
            ctx.fillStyle = '#1e1b4b';
            ctx.font = 'bold 110px system-ui, -apple-system, sans-serif';
            ctx.textAlign = 'center';
            wrapText(ctx, voucher.title || 'Complimentary Drink', W / 2, H / 2 - 80, cardW - 120, 120);

            // Subtitle
            ctx.fillStyle = '#5b21b6';
            ctx.font = '48px system-ui, -apple-system, sans-serif';
            if (voucher.subtitle) {
                wrapText(ctx, voucher.subtitle, W / 2, H / 2 + 80, cardW - 120, 56);
            }

            // Footer
            ctx.fillStyle = '#6b7280';
            ctx.font = '32px system-ui, -apple-system, sans-serif';
            if (voucher.footer) {
                ctx.fillText(voucher.footer, W / 2, H - pad - 180);
            }

            // Disclaimer
            ctx.fillStyle = '#9ca3af';
            ctx.font = 'italic 28px system-ui, -apple-system, sans-serif';
            if (voucher.disclaimer) {
                ctx.fillText(voucher.disclaimer, W / 2, H - pad - 130);
            }

            // YulOnTap footer
            ctx.fillStyle = '#a21caf';
            ctx.font = 'bold 26px system-ui, -apple-system, sans-serif';
            ctx.fillText('Powered by YulOnTap', W / 2, H - pad - 60);

            // Export & trigger download
            const url = canvas.toDataURL('image/png');
            const a = document.createElement('a');
            a.href = url;
            a.download = `voucher-${voucher.card_id || 'DEMO'}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            toast.success(t('demo.downloaded', 'Voucher downloaded'));
        } catch {
            toast.error(t('demo.downloadFailed', 'Download failed'));
        } finally {
            setDownloading(false);
        }
    };

    const submitReset = async () => {
        if (!resetPassword) {
            toast.error(t('demo.enterPassword', 'Enter the reset password'));
            return;
        }
        setResetting(true);
        try {
            const res = await fetch(`${API}/api/demo/${encodeURIComponent(cardId)}/reset`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    password: resetPassword,
                    title: resetTitle || undefined,
                    subtitle: resetSubtitle || undefined,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                toast.error(stringifyDetail(data.detail) || t('demo.resetFailed', 'Reset failed'));
                setResetting(false);
                return;
            }
            setVoucher(data);
            setRevealed(false);
            setResetOpen(false);
            setResetPassword('');
            setResetTitle('');
            setResetSubtitle('');
            toast.success(t('demo.resetOk', 'Demo reset — ready for the next client'));
        } catch {
            toast.error(t('demo.resetFailed', 'Reset failed'));
        } finally {
            setResetting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-fuchsia-950 via-purple-950 to-slate-950 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-fuchsia-400" />
            </div>
        );
    }

    return (
        <div
            className={`min-h-screen flex flex-col items-center text-white relative overflow-hidden ${ggEnabled ? 'bg-[#0A0B0E]' : 'bg-gradient-to-b from-fuchsia-950 via-purple-950 to-slate-950'}`}
            data-testid="demo-voucher-page"
        >
            {/* Gift Generator — editable merchant wallpaper with a dark scrim for legibility */}
            {ggEnabled && (
                <div className="absolute inset-0 pointer-events-none" data-testid="gg-wallpaper">
                    <img src={ggWallpaper} alt="" className="w-full h-full object-cover" fetchPriority="high" decoding="async" onError={onImgFallback} />
                    <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 50% 38%, rgba(12,14,20,0.28) 0%, rgba(10,11,14,0.72) 70%, rgba(8,9,12,0.9) 100%)' }} />
                </div>
            )}

            {/* Decorative sparkles — only on the non-Gift-Generator demo surface */}
            {!ggEnabled && (
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-10 left-8 w-2 h-2 bg-fuchsia-300 rounded-full animate-pulse" />
                <div className="absolute top-32 right-12 w-1.5 h-1.5 bg-purple-300 rounded-full animate-pulse" style={{ animationDelay: '0.6s' }} />
                <div className="absolute top-64 left-16 w-1 h-1 bg-white rounded-full animate-pulse" style={{ animationDelay: '1.2s' }} />
                <div className="absolute bottom-48 right-24 w-2 h-2 bg-fuchsia-400 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }} />
                <div className="absolute bottom-32 left-24 w-1 h-1 bg-purple-300 rounded-full animate-pulse" style={{ animationDelay: '0.9s' }} />
            </div>
            )}

            {/* Reset gear — top right (hidden when the Gift Generator experience is active) */}
            {!ggEnabled && (
            <button
                onClick={() => setResetOpen(true)}
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center transition-colors z-10"
                data-testid="demo-reset-gear"
                aria-label={t('demo.resetGear', 'Reset demo')}
            >
                <Settings className="w-4 h-4 text-white/60" />
            </button>
            )}

            <div className="w-full max-w-md px-6 pt-20 pb-10 flex-1 flex flex-col items-center justify-center relative z-10">
                {ggEnabled ? (
                    <div className="w-full flex flex-col items-center" data-testid="gg-panel">
                        {previewMode && (
                            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/40 px-4 py-1.5" data-testid="gg-preview-banner">
                                <Eye className="w-4 h-4 text-fuchsia-200" />
                                <span className="text-xs font-bold uppercase tracking-wider text-fuchsia-100">{t('giftGenerator.previewBadge')}</span>
                            </div>
                        )}
                        {/* Merchant brand badge — clean logo over wallpaper (no box).
                            A soft drop-shadow keeps a transparent logo legible. */}
                        {(merchantLogo || merchantName) && (
                            <div
                                className="mb-6 flex w-full items-center justify-center animate-in fade-in slide-in-from-top-2 duration-500"
                                data-testid="gg-merchant-logo"
                            >
                                {merchantLogo && (
                                    <img src={merchantLogo} alt={merchantName} style={{ maxHeight: `${ggLogoPx}px` }} className="h-auto w-auto max-w-full object-contain drop-shadow-[0_2px_12px_rgba(0,0,0,0.55)]" fetchPriority="high" decoding="async" onError={(e) => { if (!e.currentTarget.dataset.fb) { onImgFallback(e); return; } e.currentTarget.style.display = 'none'; }} />
                                )}
                                {!merchantLogo && merchantName && (
                                    <span className="text-base font-bold tracking-wide text-white px-4 py-2 rounded-full bg-slate-950/45 backdrop-blur-md border border-white/15">{merchantName}</span>
                                )}
                            </div>
                        )}

                        {ggPhase === 'idle' && (
                            <div className="w-full text-center space-y-7 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="gg-idle">
                                <div className="space-y-3" data-testid="gg-hero-headline">
                                    <h1 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-tight leading-snug text-white">
                                        {gx('headlineLine1')}
                                    </h1>
                                    <p className="text-4xl sm:text-5xl font-black tracking-tight text-amber-300 uppercase leading-none drop-shadow-[0_2px_16px_rgba(234,179,8,0.45)]">
                                        {gx('headlineHighlight')}
                                    </p>
                                    <p className="text-sm sm:text-base font-medium tracking-wide text-slate-300/90 max-w-xs mx-auto pt-1">
                                        {gx('tapForGift')}
                                    </p>
                                </div>
                                <button
                                    onClick={handleGenerate}
                                    data-testid="gg-generate-btn"
                                    className="w-full max-w-xs mx-auto py-5 rounded-2xl bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 text-slate-950 font-black text-lg tracking-wide uppercase shadow-2xl shadow-amber-500/40 border border-amber-300/60 hover:brightness-110 active:scale-[0.98] transition-all"
                                >
                                    <span className="inline-flex items-center gap-2">
                                        <Gift className="w-5 h-5" />
                                        {gx('generateButton')}
                                    </span>
                                </button>
                            </div>
                        )}

                        {ggPhase === 'reward_required' && (
                            <div className="w-full text-center space-y-6 py-8 animate-in fade-in duration-300" data-testid="gg-reward-required">
                                <div className="mx-auto w-16 h-16 rounded-full bg-amber-500/15 flex items-center justify-center">
                                    <Gift className="w-8 h-8 text-amber-300" />
                                </div>
                                <p className="text-base sm:text-lg font-semibold text-white max-w-xs mx-auto leading-relaxed">
                                    {t('giftGenerator.rewardRequired', 'This scratch card is a thank-you for completing the survey. Please finish the survey to unlock your scratch.')}
                                </p>
                                <button
                                    onClick={() => { window.history.length > 1 ? window.history.back() : window.location.assign('/'); }}
                                    data-testid="gg-reward-required-back-btn"
                                    className="w-full max-w-xs mx-auto py-3.5 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold border border-white/15 transition-colors"
                                >
                                    {t('giftGenerator.rewardRequiredBack', 'Go back')}
                                </button>
                            </div>
                        )}

                        {ggPhase === 'generating' && (
                            <div className="w-full text-center space-y-8 py-6 animate-in fade-in duration-300" data-testid="gg-generating">
                                <div className="relative mx-auto w-28 h-28 flex items-center justify-center">
                                    <span className="absolute inset-0 rounded-full bg-amber-500/20 animate-ping" />
                                    <span className="absolute inset-3 rounded-full border-2 border-amber-400/40 animate-pulse" />
                                    <Gift className="w-12 h-12 text-amber-300 animate-pulse relative z-10" />
                                    <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-amber-300 rounded-full animate-ping" />
                                </div>
                                <div className="space-y-1.5">
                                    <p className="text-2xl font-bold text-white">{gx('generatingTitle')}</p>
                                    <p className="text-sm text-slate-300/80">{gx('generatingSub')}</p>
                                </div>
                                <div className="w-60 h-1.5 bg-white/10 rounded-full mx-auto overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-amber-400 to-yellow-300 rounded-full animate-pulse" style={{ width: '70%' }} />
                                </div>
                            </div>
                        )}

                        {ggPhase === 'revealed' && ggSession && (
                            <div className="w-full space-y-5 animate-in fade-in zoom-in-95 duration-500" data-testid="gg-revealed">
                                <div className="text-center space-y-1.5">
                                    <p className="text-2xl sm:text-3xl font-black text-white leading-tight">{gx('revealTitle')} 🎉</p>
                                    <p className="text-sm text-slate-300/80">{gx('keepPrompt')}</p>
                                </div>

                                {/* Premium reward ticket — surface follows the org's saved
                                    Card Style (Frosted/Clear/Solid); gold frame + shadow keep
                                    the Gift Generator's personality. */}
                                <div
                                    className={`relative rounded-3xl p-6 sm:p-7 overflow-hidden ${ggCardSurface} border-2 border-amber-400/70 shadow-[0_16px_48px_rgba(0,0,0,0.6),0_0_30px_rgba(234,179,8,0.15)]`}
                                    data-testid="gg-reward-card"
                                >
                                    <div className="absolute -top-6 -right-6 w-28 h-28 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full opacity-[0.12]" />
                                    <div className="text-center space-y-3 relative">
                                        <p className="text-xs font-bold tracking-[0.3em] text-amber-300">
                                            ✦ {t('giftGenerator.badge')} ✦
                                        </p>
                                        {ggSession.gift?.image_url && (
                                            <img
                                                src={sized(ggSession.gift.image_url.startsWith('http') ? ggSession.gift.image_url : `${API}${ggSession.gift.image_url}`, 440)}
                                                alt={ggSession.gift?.title || ''}
                                                className="w-40 h-40 sm:w-44 sm:h-44 mx-auto object-cover rounded-2xl shadow-lg border-2 border-amber-300/50"
                                                decoding="async"
                                                fetchPriority="high"
                                                onError={onImgFallback}
                                                data-testid="gg-gift-image"
                                            />
                                        )}
                                        <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight" data-testid="gg-gift-title">
                                            {ggSession.gift?.title}
                                        </h2>
                                        {ggSession.gift?.description && (
                                            <p className="text-sm sm:text-base text-slate-300 leading-relaxed" data-testid="gg-gift-desc">
                                                {ggSession.gift.description}
                                            </p>
                                        )}
                                        {Number(ggSession.gift?.value) > 0 && (
                                            <p className="inline-block text-base sm:text-lg font-black tracking-wider text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded-full px-4 py-1" data-testid="gg-gift-value">
                                                ${ggSession.gift.value}
                                            </p>
                                        )}
                                    </div>
                                    {ggConfig?.reveal_method === 'scratch' && !ggScratched && (
                                        <ScratchReveal onComplete={() => { setGgScratched(true); ggTrack('scratch_completed'); }} label={t('giftGenerator.scratchHint')} logoUrl={scratchLogo} logoOpacity={scratchLogoOpacity} logoSize={scratchLogoSize} />
                                    )}
                                </div>

                                {ggConfig?.reveal_method === 'scratch' && !ggScratched && (
                                    <div className="space-y-3 -mt-1">
                                        <p className="text-center text-sm font-medium text-white/85" data-testid="gg-scratch-instruction">
                                            {gx('scratchInstruction')}
                                        </p>
                                        <button
                                            onClick={() => setGgScratched(true)}
                                            data-testid="gg-auto-reveal-btn"
                                            className="mx-auto flex items-center justify-center gap-2 min-h-[48px] px-6 rounded-xl bg-slate-900/70 hover:bg-slate-800/80 active:scale-[0.98] border border-amber-400/50 text-amber-200 font-bold text-sm uppercase tracking-wide shadow-lg transition-all"
                                        >
                                            <Eye className="w-4 h-4" />
                                            {t('giftGenerator.autoReveal')}
                                        </button>
                                    </div>
                                )}
                                {ggSession.can_exchange && (
                                    <p className="text-center text-sm text-slate-300/70" data-testid="gg-exchanges-left">
                                        {t('giftGenerator.exchangesLeft', { count: Math.max(0, (ggSession.exchange_limit || 0) - (ggSession.exchanges_used || 0)) })}
                                    </p>
                                )}
                                <div className="flex flex-col gap-2.5 pt-1">
                                    {(ggConfig?.reveal_method !== 'scratch' || ggScratched) && (
                                    <Button
                                        onClick={() => (leadGenOn ? setGgPhase('leadGen') : handleAccept(false))}
                                        data-testid="gg-accept-btn"
                                        className="w-full min-h-[52px] bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 hover:brightness-110 text-slate-950 font-black text-base uppercase tracking-wide shadow-lg shadow-amber-500/30"
                                    >
                                        <Check className="w-5 h-5 mr-2" />
                                        {gx('acceptButton')}
                                    </Button>
                                    )}
                                    {ggSession.can_exchange && (ggConfig?.reveal_method !== 'scratch' || ggScratched) && (
                                        <Button
                                            onClick={handleExchange}
                                            variant="ghost"
                                            data-testid="gg-exchange-btn"
                                            className="w-full min-h-[46px] bg-slate-800/70 hover:bg-slate-700/80 text-amber-200 font-bold uppercase tracking-wide border border-amber-500/30"
                                        >
                                            <RefreshCw className="w-4 h-4 mr-2" />
                                            {gx('exchangeButton')}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* LEAD GENERATION — one optional step between the
                            reveal and the voucher. Off unless the org turns it
                            on, in which case the voucher is minted only after
                            the buyer chooses, so the doubled prize is real. */}
                        {/* PATH B ONLY — Lead Gen ON. Path A never reaches this phase. */}
                        {ggPhase === 'leadGen' && (
                            <LeadGenOfferScreen
                                giftImageUrl={leadGenImage}
                                prizeLabel={doubledPrizeLabel}
                                onImageError={onLeadGenImgFallback}
                                onAccept={handleLeadGenAccept}
                                onDecline={() => { ggTrack('leadgen_declined'); handleAccept(false); }}
                            />
                        )}

                        {ggPhase === 'accepting' && (
                            <div className="w-full text-center space-y-6 py-10 animate-in fade-in duration-300" data-testid="gg-accepting">
                                <Loader2 className="w-11 h-11 animate-spin text-amber-300 mx-auto" />
                                <p className="text-lg font-semibold text-white">{t('giftGenerator.preparingVoucher')}</p>
                            </div>
                        )}

                        {ggPhase === 'delivering' && (
                            <div className="w-full py-6 animate-in fade-in duration-300" data-testid="gg-delivering">
                                <GiftEmailDelivery
                                    sessionId={ggSession?.session_id}
                                    path={deliveryPath}
                                    prefillEmail={ggSession?.captured_email || ''}
                                    merchantName={merchantName}
                                />
                            </div>
                        )}

                        {ggPhase === 'previewAccepted' && (
                            <div className="w-full text-center space-y-5 py-8 animate-in fade-in zoom-in-95 duration-300" data-testid="gg-preview-accepted">
                                <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center">
                                    <Check className="w-8 h-8 text-emerald-300" />
                                </div>
                                <p className="text-2xl font-black text-white">
                                    {previewDoubled ? doubledPrizeLabel : ggSession?.gift?.title}
                                </p>
                                {previewDoubled && (
                                    <p className="text-xs font-bold uppercase tracking-wider text-amber-300"
                                       data-testid="gg-preview-doubled-note">
                                        {t('leadGeneration.previewDoubled', 'Doubled via the app download step')}
                                    </p>
                                )}
                                <p className="text-sm text-slate-300/80 max-w-xs mx-auto">{t('giftGenerator.previewAcceptedNote')}</p>
                                <Button
                                    onClick={() => { setGgSession(null); setPreviewExch(0); setPreviewDoubled(false); setGgPhase('idle'); }}
                                    data-testid="gg-preview-restart-btn"
                                    className="bg-slate-800/70 hover:bg-slate-700/80 text-amber-200 font-bold uppercase tracking-wide border border-amber-500/30"
                                >
                                    {t('giftGenerator.previewRestart')}
                                </Button>
                            </div>
                        )}

                        {/* Tapelligence attribution + premium value-prop footer (real logo asset) */}
                        <div className="w-full max-w-md mt-9 space-y-4 text-center">
                            <div className="flex flex-col items-center gap-1.5">
                                <span className="text-[11px] sm:text-xs font-medium tracking-[0.25em] uppercase text-slate-400">
                                    {t('giftGenerator.poweredByLabel')}
                                </span>
                                <img
                                    src="/branding/tapelligence-logo-transparent.png"
                                    alt="Tapelligence"
                                    width={709}
                                    height={160}
                                    loading="lazy"
                                    decoding="async"
                                    className="h-9 sm:h-11 w-auto object-contain"
                                    data-testid="gg-tapelligence-logo"
                                />
                            </div>
                            <div
                                className="flex items-stretch justify-center divide-x divide-white/15 border-t border-white/10 pt-4"
                                data-testid="gg-value-prop-footer"
                            >
                                {[t('giftGenerator.valueProp1'), t('giftGenerator.valueProp2'), t('giftGenerator.valueProp3')].map((vp, i) => (
                                    <span key={i} className="px-3 text-[10px] sm:text-xs font-semibold tracking-widest uppercase text-slate-300/80 leading-tight flex items-center">
                                        {vp}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : !revealed ? (
                    <div className="w-full text-center space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="space-y-2">
                            <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-3 py-1">
                                <Sparkles className="w-3.5 h-3.5 text-fuchsia-300" />
                                <span className="text-[11px] font-semibold tracking-[0.2em] text-fuchsia-200 uppercase">
                                    {t('demo.vipBadge', 'VIP')}
                                </span>
                            </div>
                            <h1 className="text-4xl sm:text-5xl font-black leading-tight">
                                {t('demo.title', 'VIP Reward Ready')}
                            </h1>
                            <p className="text-sm text-white/60 mt-2">
                                {t('demo.subtitle', 'Tap below to reveal your voucher.')}
                            </p>
                        </div>

                        <button
                            onClick={() => setRevealed(true)}
                            data-testid="demo-press-vip-btn"
                            className="w-full max-w-xs py-5 rounded-2xl bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white font-bold text-lg tracking-wider shadow-2xl shadow-fuchsia-500/40 border border-fuchsia-400/50 hover:scale-[1.02] active:scale-[0.98] transition-transform"
                        >
                            <span className="inline-flex items-center gap-2">
                                <Gift className="w-5 h-5" />
                                {t('demo.pressVip', 'PRESS VIP')}
                            </span>
                        </button>
                    </div>
                ) : (
                    <div
                        ref={voucherRef}
                        className="w-full space-y-4 animate-in fade-in zoom-in-95 duration-500"
                        data-testid="demo-voucher-revealed"
                    >
                        <div className="text-center space-y-1">
                            <p className="text-2xl font-black">
                                {t('demo.rewardTitle', 'You\u2019ve received a reward')} 🎉
                            </p>
                            <p className="text-xs text-white/60">{t('demo.rewardSubtitle', 'Show this to your server')}</p>
                        </div>

                        {/* The voucher card itself — visually matches the PNG export */}
                        <div className="bg-white text-slate-900 rounded-3xl p-6 shadow-2xl border-4 border-fuchsia-400 relative overflow-hidden">
                            <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-fuchsia-500 to-purple-600 rounded-full opacity-10" />
                            <div className="text-center space-y-3 relative">
                                <p className="text-xs font-bold tracking-[0.3em] text-fuchsia-600">
                                    ✦ {t('demo.voucherBadge', 'VIP REWARD')} ✦
                                </p>
                                <h2 className="text-3xl sm:text-4xl font-black text-indigo-950 leading-tight" data-testid="demo-voucher-title">
                                    {voucher?.title}
                                </h2>
                                {voucher?.subtitle && (
                                    <p className="text-base text-purple-700 font-medium" data-testid="demo-voucher-subtitle">{voucher.subtitle}</p>
                                )}
                                <div className="pt-3 border-t border-slate-200 space-y-1 text-center">
                                    {voucher?.footer && (
                                        <p className="text-xs text-slate-500">{voucher.footer}</p>
                                    )}
                                    {voucher?.disclaimer && (
                                        <p className="text-[11px] italic text-slate-400">{voucher.disclaimer}</p>
                                    )}
                                </div>
                                <p className="text-[10px] font-bold text-fuchsia-600 pt-1">Powered by YulOnTap</p>
                            </div>
                        </div>

                        <div className="flex flex-col gap-2 pt-2">
                            <Button
                                onClick={downloadPng}
                                disabled={downloading}
                                data-testid="demo-download-btn"
                                className="w-full min-h-[48px] bg-white text-fuchsia-700 hover:bg-white/90 font-bold text-base"
                            >
                                {downloading
                                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    : <Download className="w-4 h-4 mr-2" />}
                                {t('demo.downloadBtn', 'Download Voucher')}
                            </Button>
                            <Button
                                onClick={() => setRevealed(false)}
                                variant="ghost"
                                data-testid="demo-back-btn"
                                className="w-full min-h-[40px] text-white/70 hover:bg-white/5"
                            >
                                <X className="w-3.5 h-3.5 mr-1" />
                                {t('demo.back', 'Back')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            <p className="text-[10px] text-white/30 pb-4 relative z-10">
                {ggEnabled ? t('giftGenerator.poweredBy') : t('demo.footer', 'Demo Mode \u00b7 Persistent Voucher')}
            </p>

            {/* Reset dialog */}
            <Dialog open={resetOpen} onOpenChange={setResetOpen}>
                <DialogContent data-testid="demo-reset-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <RotateCcw className="w-4 h-4 text-fuchsia-600" />
                            {t('demo.resetDialogTitle', 'Reset Demo Voucher')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('demo.resetDialogDesc', 'Replace the demo voucher. Enter the staff password to continue.')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1">
                            <Label htmlFor="demo-reset-pw" className="text-xs">{t('demo.password', 'Reset password')}</Label>
                            <Input
                                id="demo-reset-pw"
                                type="password"
                                value={resetPassword}
                                onChange={(e) => setResetPassword(e.target.value)}
                                data-testid="demo-reset-password"
                                autoFocus
                                onKeyDown={(e) => e.key === 'Enter' && submitReset()}
                            />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="demo-reset-title" className="text-xs">
                                {t('demo.titleLabel', 'New voucher title')}
                                <span className="text-[10px] text-slate-400 ml-1">({t('common.optional', 'optional')})</span>
                            </Label>
                            <Input
                                id="demo-reset-title"
                                placeholder={voucher?.title || 'Complimentary Drink'}
                                value={resetTitle}
                                onChange={(e) => setResetTitle(e.target.value)}
                                data-testid="demo-reset-title"
                                maxLength={60}
                            />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="demo-reset-sub" className="text-xs">
                                {t('demo.subtitleLabel', 'Subtitle')}
                                <span className="text-[10px] text-slate-400 ml-1">({t('common.optional', 'optional')})</span>
                            </Label>
                            <Input
                                id="demo-reset-sub"
                                placeholder={voucher?.subtitle || 'Courtesy of the house'}
                                value={resetSubtitle}
                                onChange={(e) => setResetSubtitle(e.target.value)}
                                data-testid="demo-reset-subtitle"
                                maxLength={80}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                            onClick={submitReset}
                            disabled={resetting || !resetPassword}
                            data-testid="demo-reset-submit"
                            className="bg-fuchsia-600 hover:bg-fuchsia-700"
                        >
                            {resetting
                                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                : <RotateCcw className="w-4 h-4 mr-2" />}
                            {t('demo.resetCta', 'Reset Demo')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ---------- Canvas helpers ----------

function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
    const words = String(text || '').split(' ');
    let line = '';
    const lines = [];
    for (const w of words) {
        const test = line ? line + ' ' + w : w;
        if (ctx.measureText(test).width > maxWidth && line) {
            lines.push(line);
            line = w;
        } else {
            line = test;
        }
    }
    if (line) lines.push(line);
    lines.forEach((ln, i) => ctx.fillText(ln, x, y + i * lineHeight));
}
